import {
  DESIGN_FOLDER_UPLOAD_MAX_DEPTH,
  DESIGN_FOLDER_UPLOAD_MAX_FILES,
  DESIGN_FOLDER_UPLOAD_MAX_FOLDERS,
  DESIGN_PATH_CHARACTER_LIMIT_FILE,
  DESIGN_PATH_CHARACTER_LIMIT_FOLDER,
} from '../../scripts/constants';
import { getInvalidFolderNameCharacters } from './folder-name-utils';

/**
 * Pure planning for a folder upload: derive each file's relative path, collect the folder tree
 * to create, enforce the client-side limits, and - once the server returns the sanitized
 * folder keys - build the final file keys. A leaf module (constants and folder-name-utils
 * only) so it stays unit testable.
 */

export interface FolderUploadFileEntry {
  file: File;
  /** The folders above the file, relative to the upload target - '' for a bare file. */
  relativeDirPath: string;
  name: string;
}

export interface FolderUploadPlan {
  /** Every folder the upload needs, ancestors included, parents before children. */
  folderRelPaths: string[];
  fileEntries: FolderUploadFileEntry[];
  /** Blocking problems - nothing should be uploaded while any exist. */
  errors: string[];
  /** Non-blocking notes, e.g. characters the server will replace with '_'. */
  warnings: string[];
}

/**
 * A dropped file carries `path` (react-dropzone's file-selector walks directories via
 * webkitGetAsEntry), a picked one `webkitRelativePath` (from an input with webkitdirectory),
 * and a plain file only its name.
 */
export const getRelativePath = (file: File): string => {
  const withPath = file as File & { path?: string; webkitRelativePath?: string };
  const raw = withPath.path || withPath.webkitRelativePath || file.name;
  return raw.replace(/^\/+/, '');
};

export const buildFolderUploadPlan = (
  files: File[],
  targetFolderFullKey: string,
): FolderUploadPlan => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const fileEntries: FolderUploadFileEntry[] = [];
  const folderRelPathSet = new Set<string>();

  if (files.length > DESIGN_FOLDER_UPLOAD_MAX_FILES) {
    errors.push(
      `A folder upload may contain at most ${DESIGN_FOLDER_UPLOAD_MAX_FILES} files; this one has ${files.length}.`,
    );
    return { folderRelPaths: [], fileEntries: [], errors, warnings };
  }

  const targetDepth = targetFolderFullKey.split('/').filter(Boolean).length;
  const warnedCharacters = new Set<string>();

  for (const file of files) {
    const relativePath = getRelativePath(file);
    const segments = relativePath.split('/').filter(Boolean);
    const name = segments[segments.length - 1] || file.name;
    const dirSegments = segments.slice(0, -1);

    if (!name.includes('.')) {
      errors.push(`"${relativePath}" has no file extension and cannot be uploaded.`);
      continue;
    }
    if (targetFolderFullKey === '/' && dirSegments.length === 0) {
      errors.push(`"${name}" must be inside a folder to be uploaded at the top level.`);
      continue;
    }
    if (targetDepth + dirSegments.length > DESIGN_FOLDER_UPLOAD_MAX_DEPTH) {
      errors.push(
        `"${relativePath}" is nested more than ${DESIGN_FOLDER_UPLOAD_MAX_DEPTH} folders deep.`,
      );
      continue;
    }
    const folderPath = dirSegments.length
      ? `${targetFolderFullKey}${dirSegments.join('/')}/`
      : targetFolderFullKey;
    if (folderPath.length > DESIGN_PATH_CHARACTER_LIMIT_FOLDER) {
      errors.push(
        `The folder path for "${relativePath}" exceeds the character limit of ${DESIGN_PATH_CHARACTER_LIMIT_FOLDER}.`,
      );
      continue;
    }
    if (`${folderPath}${name}`.length > DESIGN_PATH_CHARACTER_LIMIT_FILE) {
      errors.push(
        `The file path for "${relativePath}" exceeds the character limit of ${DESIGN_PATH_CHARACTER_LIMIT_FILE}.`,
      );
      continue;
    }
    dirSegments.forEach((segment, index) => {
      getInvalidFolderNameCharacters(segment).forEach((character) =>
        warnedCharacters.add(character),
      );
      folderRelPathSet.add(dirSegments.slice(0, index + 1).join('/'));
    });
    fileEntries.push({ file, relativeDirPath: dirSegments.join('/'), name });
  }

  if (warnedCharacters.size) {
    warnings.push(
      `Folder names may not contain ${[...warnedCharacters].join(
        ' ',
      )} - those characters will be replaced with "_".`,
    );
  }
  if (folderRelPathSet.size > DESIGN_FOLDER_UPLOAD_MAX_FOLDERS) {
    errors.push(
      `A folder upload may create at most ${DESIGN_FOLDER_UPLOAD_MAX_FOLDERS} folders; this one has ${folderRelPathSet.size}.`,
    );
  }

  const folderRelPaths = [...folderRelPathSet].sort((a, b) => {
    const depthDifference = a.split('/').length - b.split('/').length;
    return depthDifference !== 0 ? depthDifference : a.localeCompare(b);
  });
  return { folderRelPaths, fileEntries, errors, warnings };
};

export interface RemappedUploadEntry {
  file: File;
  fullKey: string;
}

/**
 * Builds each file's final full key from the server's original->sanitized folder mapping. The
 * server may rename folder segments, so client-built paths are never used for foldered files.
 */
export const remapWithSanitizedFolders = (
  fileEntries: FolderUploadFileEntry[],
  mapping: { originalRelativePath: string; sanitizedFullKey: string }[],
  targetFolderFullKey: string,
): { requests: RemappedUploadEntry[]; errors: string[] } => {
  const sanitizedByOriginal = new Map(
    mapping.map((entry) => [entry.originalRelativePath, entry.sanitizedFullKey]),
  );
  const errors: string[] = [];
  const requests: RemappedUploadEntry[] = [];
  for (const { file, relativeDirPath, name } of fileEntries) {
    if (!relativeDirPath) {
      requests.push({ file, fullKey: `${targetFolderFullKey}${name}` });
      continue;
    }
    const sanitizedFolder = sanitizedByOriginal.get(relativeDirPath);
    if (!sanitizedFolder) {
      errors.push(`The folder for "${relativeDirPath}/${name}" was not created.`);
      continue;
    }
    requests.push({ file, fullKey: `${sanitizedFolder}${name}` });
  }
  return { requests, errors };
};

export interface UploadTreeNode {
  name: string;
  children: Map<string, UploadTreeNode>;
  files: File[];
}

const emptyUploadTreeNode = (name: string): UploadTreeNode => ({
  name,
  children: new Map(),
  files: [],
});

/**
 * Rebuilds the folder tree a set of files came from, out of their relative paths, so the upload
 * dialog can show what it is about to create. Files with no folders sit at the root.
 */
export const buildUploadPreviewTree = (files: File[]): UploadTreeNode => {
  const root = emptyUploadTreeNode('');
  files.forEach((file) => {
    const segments = getRelativePath(file).split('/').filter(Boolean);
    let node = root;
    segments.slice(0, -1).forEach((folder) => {
      if (!node.children.has(folder)) node.children.set(folder, emptyUploadTreeNode(folder));
      node = node.children.get(folder)!;
    });
    node.files.push(file);
  });
  return root;
};
